import React, {useState, useRef, useEffect} from "react";
import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import ProductBrief from "./ProductBrief";
import ProductReviews from "./ProductReviews";
import CopyToClipboard from "../../utils/CopyToClipboard";
import {isAdmin} from "../../utils/Utils";
import {CampaignContext} from "../../App";

// Compact identity + actions strip. Replaces the cramped top action row.
// The full product summary opens as a popup (image excluded — it is shown here).
// props: t, selectedProduct, onEdit, onRefresh, onBack, onPrev, onNext, showNav
const DetailHeader = (props) => {
    const { t, selectedProduct: p, onEdit, onRefresh, onBack, onPrev, onNext, showNav } = props;
    const {campaigns} = React.useContext(CampaignContext);
    const [showDetails, setShowDetails] = useState(false);
    const [showReviews, setShowReviews] = useState(false);
    // Copy-to-clipboard icons next to each identifier — admins only.
    const admin = isAdmin();
    const code = (label, value) => value ? <span><b>{label}</b> {value}{admin && <CopyToClipboard textToCopy={String(value)}/>}</span> : null;
    // Own listing (is_mine) for sellers; for brand there is none, so fall back to the first
    // real seller (skip MAP/MRP reference rows) so the name still links to a live page.
    const ownComp = (p.competitors || []).find(c => c.is_mine && c.product_url)
        || (p.competitors || []).find(c => !c.is_map && !c.is_mrp && c.product_url)
        || null;
    const hasReviews = Array.isArray(p.reviews) && p.reviews.length > 0;

    // Hover quick-preview open/close. A short close delay lets the pointer travel
    // from the trigger onto the card without it disappearing; moving onto the card
    // cancels the pending close, so it only closes once the mouse is truly away.
    const [hovered, setHovered] = useState(false);
    const closeTimer = useRef(null);
    const cancelClose = () => { if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; } };
    const openCard = () => { cancelClose(); setHovered(true); };
    const scheduleClose = () => { cancelClose(); closeTimer.current = setTimeout(() => setHovered(false), 180); };
    useEffect(() => cancelClose, []); // clear any pending timer on unmount

    return <div className="pux-detailhead">
        {/* The image is the trigger for the hover quick-preview (anchored to this row). */}
        <div className="pux-detailhead-id">
            {p.image && <img className="pux-detailhead-img" referrerPolicy="no-referrer" src={p.image} alt=""
                             onMouseEnter={openCard} onMouseLeave={scheduleClose}/>}
            <div className="pux-detailhead-main">
                <div className="pux-detailhead-namerow">
                    {/* Name links to my own listing (is_mine); the Product Brief keeps the PriceRest link.
                        Admins get the PriceRest link instead — they work across customer accounts, where
                        the merchant page is not what they are after and is a link out of the panel. */}
                    <span className="pux-detailhead-name">
                        <a target="_blank" rel="noopener noreferrer"
                           href={!admin && ownComp ? ownComp.product_url : ("/#/productdetail?id=" + p.id)} title={p.name}>{p.name}</a>
                    </span>
                    {admin && <CopyToClipboard textToCopy={p.name}/>}
                    <Button label={t('actions.edit')} icon="pi pi-pencil"
                            className="p-button-outlined p-button-success p-button-sm pux-detailhead-edit" onClick={onEdit}/>
                </div>
                <span className="pux-detailhead-codes">
                    {code(t('product.sku') + ':', p.sku)}
                    {code(t('product.mpn') + ':', p.mpn)}
                    {code(t('product.barcode') + ':', p.barcode)}
                    {code(t('product.ean') + ':', p.ean)}
                    {code(t('product.upc') + ':', p.upc)}
                    {code(t('product.asin') + ':', p.asin)}
                    {code(t('product.ref_id') + ':', p.ref_id)}
                    {p.catalog_target_product_id &&
                        <a target="_blank" rel="noopener noreferrer" style={{marginRight: 8}}
                           href={"/#/productdetail?id=" + p.catalog_target_product_id}>
                            {t('product_detail.catalog_target', {
                                campaign: ((campaigns || []).find(c => c.id === (p.campaign && p.campaign.settings
                                    && p.campaign.settings.catalog_of_campaign_id)) || {}).name || '',
                                defaultValue: '{{campaign}} → product'})}
                        </a>}
                    <Button label={t('product_detail.details', 'Details')} icon="pi pi-info-circle"
                            className="p-button-link p-button-sm" style={{padding:0}} onClick={() => setShowDetails(true)}/>
                    {hasReviews && ownComp &&
                        <Button label={String(p.reviews.length)} icon="pi pi-comments"
                                tooltip={t('product_detail.reviews')} tooltipOptions={{position:'bottom'}}
                                className="p-button-link p-button-sm" style={{padding:0}} onClick={() => setShowReviews(true)}/>}
                </span>
            </div>

            {/* Hover quick-preview: the full Product Brief (with image). Rendered only while
                hovered so its effects (template lookup, scrape status) don't run otherwise. */}
            {hovered && <div className="pux-hovercard" onMouseEnter={openCard} onMouseLeave={scheduleClose}>
                <ProductBrief selectedProduct={p} refreshProducts={onRefresh} showCompetitionView={false}/>
            </div>}
        </div>
        <div className="pux-detailhead-actions">
            {showNav && <>
                <Button label={t('product_detail.back_to_list')} icon="pi pi-angle-double-left"
                        className="p-button-outlined p-button-help p-button-sm pux-detailhead-back" onClick={onBack}/>
                <span style={{display:'flex', gap:'.25em'}}>
                    <Button tooltip={t('actions.previous')} tooltipOptions={{position:'bottom'}}
                            icon="pi pi-angle-left" className="p-button-help p-button-sm" onClick={onPrev}/>
                    <Button tooltip={t('actions.next')} tooltipOptions={{position:'bottom'}}
                            icon="pi pi-angle-right" className="p-button-help p-button-sm" onClick={onNext}/>
                </span>
            </>}
        </div>

        {/* Details = Product Brief, with the description shown inline right below it (if any). */}
        <Dialog header={t('product_detail.details', 'Details')} visible={showDetails} modal
                style={{width:'480px', maxWidth:'95vw'}} onHide={() => setShowDetails(false)}>
            <ProductBrief selectedProduct={p} refreshProducts={onRefresh} showCompetitionView={false} hideImage/>
            {p.description &&
                <div style={{marginTop:'12px'}}>
                    <div className="pux-section-title"><i className="pi pi-align-left"/> {t('product_detail.description')}</div>
                    <div className="pux-detail-desc" dangerouslySetInnerHTML={{__html: p.description}}/>
                </div>}
        </Dialog>

        {/* Reviews open from the review icon next to Details, in their own popup. */}
        {hasReviews && ownComp &&
            <Dialog header={t('product_detail.reviews')} visible={showReviews} modal
                    style={{width:'560px', maxWidth:'95vw'}} onHide={() => setShowReviews(false)}>
                <ProductReviews selectedProduct={p} myProduct={ownComp}/>
            </Dialog>}
    </div>;
};
export default DetailHeader;
